import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule, Provider } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ScrollToModule } from '@hkjeffchan/ngx-scroll-to';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EntityDataModule } from '@ngrx/data';
import { EffectsModule } from '@ngrx/effects';
import { FullRouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import mixpanel from 'mixpanel-browser';
import { RecaptchaModule } from 'ng-recaptcha';

import { AppStoreRedirectComponent } from '@app/app-store-redirect/app-store-redirect.component';
import { AuthEffects } from '@app/auth/auth.effects';
import { StoreDevModules } from '@app/build-specifics/devtool';
import { Auth0AnalyticsService } from '@app/core/auth0-analytics.service';
import { Auth0ClientService } from '@app/core/auth0-client.service';
import { FeatureFlagEffects } from '@app/core/feature-flags/feature-flag.effects';
import { MixpanelService } from '@app/core/mixpanel.service';
import { RetrieveAccessTokenGraphQLService } from '@app/core/retrieve-access-token-graphql.service';
import { InjectorModule } from '@app/injector.module';
import { MembershipRenewalInterstitialEffects } from '@app/membership/membership-renewal-interstitial.effects';
import { OnboardingPromptsAnalyticsEffects } from '@app/onboarding/onboarding-prompts-analytics.effects';
import { SharedEffects } from '@app/shared/shared-effects';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { MyOneApp } from './app.component';
import { metaReducers, reducers } from './app.reducer';
import { AppointmentBookingSessionService } from './appointment/appointment-booking-session.service';
import { AuthInterceptor } from './core/auth-interceptor';
import { CoreModule } from './core/core.module';
import { SentryErrorHandler } from './core/errors/sentry';
import { GraphQLModule } from './graphql.module';
import { SharedModule } from './shared/shared.module';
import { WindowService } from './utils/window.service';

const initMixpanelService = (): MixpanelService => {
  mixpanel.init(environment.mixpanelToken, { cross_subdomain_cookie: environment.mixpanelCrossDomain });
  mixpanel.set_config({ debug: environment.mixpanelDebug });
  return new MixpanelService(mixpanel);
};

const providers: Provider[] = [
  AppointmentBookingSessionService,
  Title,
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useExisting: Auth0ClientService, // Do not put any interceptors after this one. They will be ignored.
    multi: true,
  },
  { provide: MixpanelService, useFactory: initMixpanelService },
  {
    provide: APP_INITIALIZER,
    useFactory: Auth0ClientService.initializeAuth0Client,
    multi: true,
    deps: [Auth0ClientService, Auth0AnalyticsService, WindowService, RetrieveAccessTokenGraphQLService],
  },
];

if (environment.sentry.enabled) {
  providers.push({
    provide: ErrorHandler,
    useValue: SentryErrorHandler,
  });
}

@NgModule({
  declarations: [MyOneApp, AppStoreRedirectComponent],
  imports: [
    InjectorModule,
    RouterModule,
    BrowserModule,
    BrowserAnimationsModule,
    ServiceWorkerModule.register('/ngsw-worker.js', { enabled: false }),
    HttpClientModule,
    CoreModule,
    SharedModule,
    AppRoutingModule,
    NgbModule,
    ScrollToModule.forRoot(),
    RecaptchaModule,
    GraphQLModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    GoogleTagManagerModule.forRoot({
      id: environment.googleTagManagerId,
    }),
    EffectsModule.forRoot([
      AuthEffects,
      FeatureFlagEffects,
      MembershipRenewalInterstitialEffects,
      OnboardingPromptsAnalyticsEffects,
      SharedEffects,
    ]),
    EntityDataModule.forRoot({}),
    StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer }),
    StoreDevModules,
    ReactiveFormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
  ],
  providers: providers,
  bootstrap: [MyOneApp],
})
export class MyOneAppModule {}
